/**
 * DocumentsPage — RC 1.41 Phase C
 *
 * Library-only workspace. Three read-only tabs:
 *   1. Source CV       — single active Source CV (Replace flow lives here)
 *   2. CV Variants     — historical list with preview + delete + export
 *   3. Cover Letters   — historical list with preview + delete + export
 *
 * Generation is intentionally NOT available on this page. To generate
 * a tailored CV or cover letter, the user opens a job and uses the
 * Job Application Wizard. This is the RC 1.41 product contract:
 * `/documents` is library only; `/jobs/:id` owns application creation.
 */

import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import CvLibraryPage from './CvLibraryPage';
import VariantsHistory from '@/components/library/VariantsHistory';
import CoverLettersHistory from '@/components/library/CoverLettersHistory';
import InterviewPrepHistory from '@/components/library/InterviewPrepHistory';

const TABS = [
  { key: 'source',   label: 'Source CV',      Component: CvLibraryPage },
  { key: 'variants', label: 'CV Variants',    Component: VariantsHistory },
  { key: 'letters',  label: 'Cover Letters',  Component: CoverLettersHistory },
  { key: 'prep',     label: 'Interview Prep', Component: InterviewPrepHistory },
];

export default function DocumentsPage() {
  const [params, setParams] = useSearchParams();
  // RC 1.41 Phase C — default tab is now 'source'. The legacy
  // 'uploaded' tab key continues to resolve to Source CV so any old
  // deep-links keep working.
  const requested = params.get('tab') || 'source';
  const active = requested === 'uploaded' ? 'source' : requested;
  const Current = useMemo(
    () => TABS.find(t => t.key === active)?.Component || CvLibraryPage,
    [active],
  );

  return (
    <div className="space-y-6" data-testid="documents-workspace">
      <header className="flex flex-col gap-1">
        <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">Library</p>
        <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold text-ndor-ink tracking-tight">
          Documents
        </h1>
        <p className="text-sm text-ndor-ink-secondary">
          Browse your Source CV, generated variants, cover letters, and interview prep.
        </p>
      </header>

      <Card className="border-ndor-edge bg-ndor-surface">
        <CardContent className="p-0">
          <nav
            className="flex border-b border-ndor-edge"
            data-testid="documents-tabs"
            role="tablist"
          >
            {TABS.map(t => {
              const isActive = t.key === active;
              return (
                <Link
                  key={t.key}
                  to={`/documents?tab=${t.key}`}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`documents-tab-${t.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const next = new URLSearchParams(params);
                    next.set('tab', t.key);
                    setParams(next);
                  }}
                  className={`relative flex-1 text-center px-4 py-4 text-sm transition-colors
                    ${isActive
                      ? 'text-ndor-ink font-semibold bg-ndor-surface-alt/50'
                      : 'text-ndor-ink-secondary hover:text-ndor-ink hover:bg-ndor-surface-alt/30'}`}
                >
                  <span className="tracking-tight">{t.label}</span>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-6 right-6 bottom-0 h-[2px] bg-ndor-gold rounded-full"
                    />
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 sm:p-6" data-testid={`documents-panel-${active}`}>
            <Current />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
